import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

export const LiveClock: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;

          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await res.json();

            if (data?.address) {
              const city =
                data.address.city ||
                data.address.town ||
                data.address.village ||
                data.address.suburb ||
                data.address.hamlet ||
                data.address.county ||
                data.address.state;

              const country = data.address.country;
              setLocation(city ? `${city}, ${country}` : country || null);
            }
          } catch (err) {
            console.error('Geolocation lookup failed:', err);
          }
        },
        (err) => {
          console.warn('Geolocation denied:', err);
          setLocation(null);
        }
      );
    }

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) =>
    date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

  const formatDate = (date: Date) =>
    date.toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });

  const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.primary.light, 0.12)} 100%)`,
          padding: '8px 16px',
          borderRadius: '12px',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: 'blur(10px)',
          '@media (max-width: 768px)': {
            gap: 1,
            padding: '6px 12px'
          }
        }}
      >
        {/* Time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon
            sx={{
              fontSize: '18px',
              color: theme.palette.primary.main,
              '@media (max-width: 768px)': {
                fontSize: '16px'
              }
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontFamily: 'monospace',
              fontSize: '14px',
              '@media (max-width: 768px)': {
                fontSize: '12px'
              }
            }}
          >
            {formatTime(currentTime)}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ height: '20px', alignSelf: 'center' }} />

        {/* Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon
            sx={{
              fontSize: '16px',
              color: theme.palette.primary.main,
              '@media (max-width: 768px)': {
                fontSize: '14px'
              }
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
              fontSize: '13px',
              '@media (max-width: 768px)': {
                fontSize: '11px'
              }
            }}
          >
            {formatDate(currentTime)}
          </Typography>
        </Box>

        {/* Location or Timezone */}
        {/* <Tooltip title={location || getTimezone()} arrow>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              fontSize: '10px',
              cursor: 'help',
              '@media (max-width: 768px)': {
                display: 'none'
              }
            }}
          >
            {location ? location : getTimezone().split('/').pop()}
          </Typography>
        </Tooltip> */}
      </Box>
    </motion.div>
  );
};
